import React, { useEffect, Suspense, lazy, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { fetchUserPlugins, userPluginToInterface } from './services/userPluginsService';
import { evaluatePluginCode } from './utils/pluginEvaluator';
import { pluginRegistry } from './utils/PluginRegistry';
import { API_CONFIG } from './config/api.config';

// Contextes légers (Auth, Files) - Restent statiques pour l'initialisation
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FileManagerProvider } from './contexts/FileManagerContext';
import AutoTranslateWrapper from './components/AutoTranslateWrapper';
import Navigation from './components/Navigation'; 

// ❌ SUPPRESSION de l'import statique de VideoEditorProvider
// import { VideoEditorProvider } from './contexts/VideoEditorContext';

// ⚡️ LAZY LOADING : Le fournisseur vidéo (lourd) n'est chargé que si nécessaire
const VideoEditorProvider = lazy(() => import('./contexts/VideoEditorContext').then(m => ({ default: m.VideoEditorProvider })));

// ⚡️ LAZY LOADING DES PAGES
const BookEditor = lazy(() => import('./BookEditor')); 
const HomeList = lazy(() => import('./components/HomeList')); 
const BookManager = lazy(() => import('./components/BookManager'));
const ResetPasswordPage = lazy(() => import('./components/ResetPasswordPage'));
const GoogleAuthCallback = lazy(() => import('./components/GoogleAuthCallback'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const SharedContainer = lazy(() => import('./pages/SharedContainer'));
const SharedContainerPage = lazy(() => import('./pages/SharedContainerPage'));
const EventCheckInPage = lazy(() => import('./pages/EventCheckInPage'));

const ContainerAdminPage = lazy(() => import('./pages/ContainerAdminPage'));
const LandingPageComponent = lazy(() => import('./pages/LandingPage'));

const CookiesPage = lazy(() => import('./pages/legal/cookies'));
const CguCgvPage = lazy(() => import('./pages/legal/cgu-cgv'));
const RgpdPage = lazy(() => import('./pages/legal/rgpd'));

// --- COMPOSANT DE CHARGEMENT ---
const PageLoader = () => (
  <div className="d-flex flex-column justify-content-center align-items-center vh-100 bg-light">
    <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
      <span className="visually-hidden">Chargement...</span>
    </div>
    <div className="text-muted animate-pulse">Chargement de FlexiHub...</div>
  </div>
);

// --- COMPOSANTS HELPER ---

function DashboardPage() {
  const navigate = useNavigate();

  const handleSelectBook = (bookId: string | null, initialData?: any) => {
    const params = new URLSearchParams();
    if (bookId) params.set('bookId', bookId);
    if (initialData) params.set('initialData', JSON.stringify(initialData));
    navigate(`/editor?${params.toString()}`);
  };

  const handleCreateBook = () => {
    navigate('/editor');
  };

  return (
    <BookManager 
      onSelectBook={handleSelectBook}
      onCreateBook={handleCreateBook}
    />
  );
}

function EditorPage() {
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const bookId = urlParams.get('bookId');
  const initialDataParam = urlParams.get('initialData');
  
  let initialData = null;
  if (initialDataParam) {
    try {
      initialData = JSON.parse(initialDataParam);
    } catch (error) {
      console.error('Erreur parsing initialData:', error);
    }
  }

  const handleBackToList = () => {
    navigate('/dashboard');
  };

  // ✅ L'éditeur est maintenant enveloppé localement par ses providers lourds
  return (
    <Suspense fallback={<PageLoader />}>
      <VideoEditorProvider>
        <BookEditor 
          bookId={bookId}
          initialData={initialData}
          onBackToList={handleBackToList}
        />
      </VideoEditorProvider>
    </Suspense>
  );
}

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  return children;
}



// Composant principal (Router Guard)
function MainApp() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (user) {
    return <HomeList />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <LandingPageComponent />
    </Suspense>
  );
}

// --- APP PRINCIPALE ---
function App() {
  useEffect(() => {
    // Chargement dynamique des plugins built-in
    import('./plugins').then(module => {
      module.registerAllPlugins();
    }).catch(err => console.error("Erreur chargement plugins:", err));

    // Chargement des plugins utilisateur depuis la BD (disponibles dans tout PluginRenderer)
    fetchUserPlugins()
      .then(async plugins => {
        for (const p of plugins) {
          if (p.pluginFormat === 'html') {
            // Plugin HTML/iframe — rendu direct dans un iframe, pas de Babel
            pluginRegistry.upsert(userPluginToInterface(p));
          } else {
            // Plugin React — évaluation Babel à runtime
            let evaluated: any = null;
            try { evaluated = await evaluatePluginCode(p.pluginCode); } catch {}
            pluginRegistry.upsert(userPluginToInterface(p, evaluated));
          }
        }
      })
      .catch(() => {}); // silencieux si non connecté
  }, []);

  // Gestion email vérifié
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('verified') === '1') {
      const email = params.get('email');
      // ... (code notification inchangé)
      const messageDiv = document.createElement('div');
      messageDiv.style.cssText = `
        position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
        background: #10b981; color: white; padding: 15px 25px; border-radius: 8px;
        box-shadow: 0 4px 6px rgba(0,0,0,0.1); z-index: 9999; animation: slideDown 0.3s ease;
      `;
      messageDiv.innerHTML = `✅ Email ${email} vérifié !`;
      document.body.appendChild(messageDiv);
      setTimeout(() => document.body.removeChild(messageDiv), 5000);
      window.history.replaceState({}, document.title, '/');
    }
  }, []);
  
  // Styles globaux
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideDown { from { transform: translate(-50%, -100%); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }
      .animate-pulse { animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
      @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .5; } }
    `;
    document.head.appendChild(style);
  }, []);

  return (
    <AuthProvider>
      {/* On garde FileManager ici s'il est utilisé dans le Dashboard, sinon le descendre aussi */}
      <FileManagerProvider>
        <AutoTranslateWrapper>
          <Router>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Auth */}
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/auth/google" element={<GoogleAuthCallback />} />
                <Route path="/auth/google/success" element={<GoogleAuthCallback />} />
                <Route path="/auth/callback" element={<GoogleAuthCallback />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                {/* Admin & Core */}
                <Route path="/admin" element={<AdminPanel />} />
                <Route path="/dashboard" element={<RequireAuth><DashboardPage /></RequireAuth>} />
                
                {/* ⚡️ LA ROUTE EDITOR INCLUT MAINTENANT SON PROPRE PROVIDER VIDÉO */}
                <Route path="/editor" element={<RequireAuth><EditorPage /></RequireAuth>} />

                {/* Public Containers */}
                <Route path="/container/:containerId" element={<SharedContainer />} />
                <Route path="/shared/container/:id" element={<SharedContainerPage />} />
                <Route path="/event-booking/check-in/:slug" element={<EventCheckInPage />} />

                {/* Container Admin */}
                <Route path="/container-admin/:bookId" element={<RequireAuth><ContainerAdminPage /></RequireAuth>} />

                {/* Legal */}
                <Route path="/legal/cookies" element={<CookiesPage />} />
                <Route path="/legal/cgu-cgv" element={<CguCgvPage />} />
                <Route path="/legal/rgpd" element={<RgpdPage />} />

                {/* Home */}
                <Route path="/" element={<MainApp />} />
              </Routes>
            </Suspense>
          </Router>
        </AutoTranslateWrapper>
      </FileManagerProvider>
    </AuthProvider>
  );
}

export default App;
